import { useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { ApiError, apiFetch } from '@/lib/apiClient'
import { setAuthItem } from '@/lib/authStorage'
import { HttpError } from '@/lib/http'
import type { UserProfile, ContainerAccess } from '@/types/session'
import type { WorkspacePlanTier } from '../hooks/useWorkspacePlanTier'
import { createSpace } from '@/lib/api/endpoints/spaces'
import { TEAM_MIN_SEATS } from './billing-constants'
import type { Billing } from './billing-constants'
import { BillingToggle } from './BillingToggle'
import { SeatStepper } from './SeatStepper'
import { PlanCard } from './PlanCard'
import { usePaymentConfig } from '../hooks/usePaymentConfig'

const isNativeMobile = Capacitor.isNativePlatform()

export type PlanPickerContext = 'first_login' | 'upgrade' | 'add_workspace'

interface PlanPickerProps {
  user: UserProfile
  authToken: string | null
  onError: (msg: string | null) => void
  /**
   * 'first_login'   — new free signup; Free CTA accepts agreements and lands in app.
   * 'upgrade'       — existing user clicked Upgrade; Free CTA closes the gate.
   * 'add_workspace' — existing user adds another workspace; shows a name field and
   *                   creates the workspace first, then (for paid plans) checks out
   *                   targeting the new workspace id. No agreements step (already a
   *                   logged-in user), no per-card terms.
   */
  context: PlanPickerContext
  /** Called when the user dismisses the gate (upgrade context only). Skips full page reload. */
  onDismiss?: () => void
  /** Modal-tuned density — the PlanChoice modal passes true (smaller cards, no fullscreen header). */
  compact?: boolean
  /** The user's workspaces — drives the add_workspace #4837 "already own a free one" disable. */
  containers?: ContainerAccess[]
  /** add_workspace: true while `containers` is still hydrating, so the Free gate isn't trusted yet (#4837). */
  containersLoading?: boolean
  /** add_workspace: called with the new workspace id after a Free create (paid plans redirect to checkout). */
  onWorkspaceCreated?: (spaceId: string) => void
}

interface AcceptAgreementsResponse {
  success?: boolean
  accessToken?: string
  error?: string
}

interface CheckoutResponse {
  url?: string
  sessionId?: string
}

export function PlanPicker({
  user,
  authToken,
  onError,
  context,
  onDismiss,
  compact = false,
  containers,
  containersLoading = false,
  onWorkspaceCreated,
}: PlanPickerProps) {
  const [submitting, setSubmitting] = useState<WorkspacePlanTier | null>(null)
  const [billing, setBilling] = useState<Billing>('monthly')
  const [teamSeats, setTeamSeats] = useState<number>(TEAM_MIN_SEATS)
  const [name, setName] = useState('')
  // Set when upgrading an EXISTING workspace to Team trips the exposure gate
  // (TYPE_FLIP_NOT_CONFIRMED) — drives the Confirm dialog. New/first-login
  // workspaces are empty, so they auto-confirm and never set this.
  const [confirmTeamExposure, setConfirmTeamExposure] = useState(false)
  // add_workspace: a paid create made a (still-unpaid) workspace whose checkout
  // then failed. Reuse it on retry instead of creating another (bounds orphaned
  // pending_payment workspaces — there is no client delete endpoint).
  const pendingWorkspaceRef = useRef<{ id: string; plan: 'pro' | 'team' } | null>(null)
  const isUpgradeContext = context === 'upgrade'
  const isAddWorkspace = context === 'add_workspace'
  // A LAPSED trial user has already consumed their one trial and is no longer
  // active (expired OR in the read-only grace window). PDR 011 D15 — no second
  // free trial — so the Trial card is greyed out + unselectable for them; they
  // convert via Pro/Team. A brand-new identity (trialUsed=false) or a currently-
  // active trial (isTrialActive=true) is NOT lapsed and keeps the Trial card.
  const trialLapsed =
    Boolean(user.trialStatus?.trialUsed) && !user.trialStatus?.isTrialActive
  // #4836 — Team is server-gated by ENABLE_TEAM_BILLING. Probe /payment/config.
  const { teamBillingEnabled, isResolved: teamBillingResolved } = usePaymentConfig(authToken)
  // Hide Team when team billing is off. In add_workspace, default hidden until
  // the probe confirms it's on (matches the retired AddWorkspaceModal). In the
  // always-shown upgrade/first_login paths, hide ONLY on a confirmed-off probe —
  // never during loading or a transient /payment/config outage (which would
  // otherwise vanish the Team option a user came specifically to buy).
  const hideTeam = isAddWorkspace
    ? !teamBillingEnabled
    : teamBillingResolved && !teamBillingEnabled
  // #4837 — the free-workspace limit is a LIVE COUNT (one active free workspace
  // per owner). Disable the Free card while the user already owns one; it
  // re-enables once they own zero (upgrade/remove frees the slot). Server 409
  // FREE_WORKSPACE_LIMIT stays the backstop (and is surfaced via handleErr).
  // NOTE: ContainerAccess carries no status, so a suspended free workspace still
  // counts here — the disabled-card note steers the user to remove it.
  const ownsFree = (containers ?? []).some(
    (c) => c.isOwner && c.planTier === 'free',
  )

  function setStorageToken(token: string | undefined) {
    if (!token) return
    // Persist via setAuthItem so the rotated token lands in the platform-canonical
    // store (localStorage on native, sessionStorage on web). A raw sessionStorage
    // write was read back as stale on native after the reload that follows token
    // rotation, since the reader now uses localStorage. (Shared by PlanGate and
    // PlanChoiceModal, which both render this PlanPicker.)
    setAuthItem('xavior_auth_token', token)
  }

  async function acceptAgreements(): Promise<AcceptAgreementsResponse | null> {
    const result = await apiFetch<AcceptAgreementsResponse>(
      'auth/accept-agreements',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        authToken,
      },
    )
    // (The legacy onboarding state-machine advance that lived here was removed
    // with the 5-state machine — recipe-session completion now owns onboarding
    // progress, so the plan-pick gate no longer pokes /onboarding/advance.)
    return result
  }

  function handleErr(err: unknown) {
    if (err instanceof ApiError) {
      // apiFetch (create-checkout / accept-agreements) throws ApiError.
      const payload = err.payload as { error?: string } | undefined
      onError(payload?.error ?? 'Request failed. Please try again.')
    } else if (err instanceof HttpError) {
      // createSpace → http.post throws HttpError; the structured server body
      // (the 409 FREE_WORKSPACE_LIMIT backstop, upgrade hints) lives in `body`,
      // not `payload` (a string). Surface the actionable server copy.
      const body = err.body as { error?: string; upgradeRequired?: boolean } | undefined
      if (body?.upgradeRequired) {
        onError(String(body.error || 'Workspace limit reached. Upgrade to create more workspaces.'))
      } else if (body?.error) {
        onError(body.error)
      } else {
        onError(err.message || 'Request failed. Please try again.')
      }
    } else {
      onError('Request failed. Please try again.')
    }
  }

  /**
   * Shared Stripe checkout-redirect tail used by both the upgrade/first_login
   * (startPaid) and add_workspace (createThenCheckout) paths — keeps the
   * native-mobile guard, the create-checkout call, and the url/sessionId
   * redirect in one place so they can't drift. Returns true if it navigated
   * away (or will); false if it short-circuited (native guard / no url), in
   * which case the caller resets `submitting`. apiFetch errors propagate to the
   * caller's catch (so the TYPE_FLIP_NOT_CONFIRMED branch still works).
   */
  async function redirectToCheckout(
    body: {
      plan: 'pro' | 'team'
      billing: Billing
      quantity?: number
      confirmExposure?: boolean
      workspaceId?: string
    },
    token: string,
  ): Promise<boolean> {
    if (isNativeMobile) {
      onError('Please complete this purchase on the web. Mobile checkout for this plan is coming soon.')
      return false
    }
    const response = await apiFetch<CheckoutResponse>('payment/create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      authToken: token,
    })
    if (response?.url) {
      window.location.href = response.url
      return true
    }
    if (response?.sessionId) {
      window.location.href = `https://checkout.stripe.com/pay/${response.sessionId}`
      return true
    }
    onError('Unable to prepare checkout. Please try again.')
    return false
  }

  async function startTrial() {
    // Upgrade context: user is already on Trial/Free + has agreements. CTA is
    // "Stay on Trial" — close the gate without a page reload so it doesn't
    // immediately reappear. Strip ?upgrade=1 if present.
    if (isUpgradeContext) {
      if (onDismiss) {
        const url = new URL(window.location.href)
        url.searchParams.delete('upgrade')
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}` || '/')
        onDismiss()
      } else {
        const url = new URL(window.location.href)
        url.searchParams.delete('upgrade')
        window.location.href = `${url.pathname}${url.search}${url.hash}` || '/'
      }
      return
    }

    // First-login context: clicking Start Trial implicitly accepts agreements
    // (Pattern A — disclosure shown under the CTA button). Server still
    // records the acceptance via /auth/accept-agreements.
    if (!authToken) {
      onError('Your session expired—please log in again to continue.')
      return
    }
    setSubmitting('trial')
    onError(null)
    try {
      const response = await acceptAgreements()
      setStorageToken(response?.accessToken)
      // Hold a deterministic "setting up" loader across the full reload so the
      // gate chain can't flash a blank before session/plan/container state settles
      // (W6). Cleared by AppContent once the app is ready.
      try {
        sessionStorage.setItem('xavior:entering-app', '1')
      } catch {
        /* private mode — proceed without the loader hold */
      }
      window.location.reload()
    } catch (err) {
      handleErr(err)
      setSubmitting(null)
    }
  }

  async function startPaid(plan: 'pro' | 'team', confirmExposure = false) {
    if (!authToken) {
      onError('Your session expired—please log in again to continue.')
      return
    }
    setSubmitting(plan)
    onError(null)
    try {
      const ack = isUpgradeContext ? null : await acceptAgreements()
      setStorageToken(ack?.accessToken)

      const body: { plan: 'pro' | 'team'; billing: Billing; quantity?: number; confirmExposure?: boolean } = { plan, billing }
      if (plan === 'team') {
        body.quantity = teamSeats
        // First-login picks Team for a brand-new, empty workspace — nothing to
        // expose, so auto-confirm. Upgrading an EXISTING workspace requires the
        // explicit confirm step (gate → Confirm dialog → retry with true).
        if (!isUpgradeContext || confirmExposure) {
          body.confirmExposure = true
        }
      }

      const navigated = await redirectToCheckout(body, ack?.accessToken ?? authToken)
      if (!navigated) setSubmitting(null)
    } catch (err) {
      // Upgrading an existing workspace to Team needs a one-time exposure
      // acknowledgement — show a Confirm dialog instead of a dead-end error.
      if (
        err instanceof ApiError &&
        (err.payload as { code?: string } | undefined)?.code === 'TYPE_FLIP_NOT_CONFIRMED'
      ) {
        setConfirmTeamExposure(true)
        setSubmitting(null)
        return
      }
      handleErr(err)
      setSubmitting(null)
    }
  }

  // add_workspace: create the workspace FIRST (to get its id), then — for paid
  // plans — start a Stripe checkout bound to that new id. No agreements step
  // (the user is already logged in). Mirrors the retired AddWorkspaceModal flow.
  async function createThenCheckout(plan: 'free' | 'pro' | 'team') {
    if (!authToken) {
      onError('Your session expired—please log in again to continue.')
      return
    }
    setSubmitting(plan)
    onError(null)
    try {
      // POST /workspaces hard-requires `name`; default a blank field.
      const trimmed = name.trim() || 'New Workspace'

      if (plan === 'free') {
        const space = await createSpace(authToken, { plan, name: trimmed })
        // Reset before handing off so the CTA never sticks on "Creating…" if a
        // caller's onWorkspaceCreated doesn't unmount the picker.
        setSubmitting(null)
        onWorkspaceCreated?.(space.id)
        return
      }

      // Reuse a still-unpaid workspace from a prior failed checkout of this same
      // plan rather than creating another (bounds orphaned pending_payment rows).
      let workspaceId: string
      if (pendingWorkspaceRef.current?.plan === plan) {
        workspaceId = pendingWorkspaceRef.current.id
      } else {
        const space = await createSpace(authToken, {
          plan,
          name: trimmed,
          ...(plan === 'team' ? { seats: teamSeats } : {}),
        })
        workspaceId = space.id
        pendingWorkspaceRef.current = { id: space.id, plan }
      }

      const body: {
        plan: 'pro' | 'team'
        billing: Billing
        quantity?: number
        confirmExposure?: boolean
        workspaceId: string
      } = { workspaceId, plan, billing }
      if (plan === 'team') {
        // Brand-new empty workspace — nothing to expose, so auto-confirm.
        body.quantity = teamSeats
        body.confirmExposure = true
      }

      const navigated = await redirectToCheckout(body, authToken)
      if (!navigated) setSubmitting(null)
    } catch (err) {
      handleErr(err)
      setSubmitting(null)
    }
  }

  // Paid-plan CTAs differ by context: add_workspace creates a new workspace
  // first; upgrade/first_login check out the active workspace.
  const onProCta = isAddWorkspace ? () => createThenCheckout('pro') : () => startPaid('pro')
  const onTeamCta = isAddWorkspace ? () => createThenCheckout('team') : () => startPaid('team')

  return (
    <div className={compact ? 'w-full' : 'w-full max-w-6xl'}>
      {confirmTeamExposure && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirmTeamExposure(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">Share this workspace with your team?</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-600">
              Upgrading to Team makes everything currently in this workspace visible to the people you
              invite. Your connected email, calendar, and drive stay private to you.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                onClick={() => setConfirmTeamExposure(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting !== null}
                className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-600 disabled:opacity-60"
                onClick={() => { setConfirmTeamExposure(false); void startPaid('team', true) }}
              >
                {submitting === 'team' ? 'Starting…' : 'Confirm & continue'}
              </button>
            </div>
          </div>
        </div>
      )}
      {!compact && (
        <div className="mb-8 text-center md:mb-10">
          <div className="mb-4 inline-flex items-center justify-center md:mb-6">
            <img src="/favicon.svg" alt="XAVIOR" className="h-10 w-10 md:h-12 md:w-12" />
          </div>
          <h1 className="text-2xl font-medium leading-tight tracking-tight text-gray-900 md:text-4xl">
            {isUpgradeContext
              ? 'Pick the plan that fits.'
              : 'Free for 14 days. Private when it matters.'}
          </h1>
          {isUpgradeContext ? (
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-gray-600">
              {trialLapsed
                ? 'Your free trial has ended. Pick a plan to keep your workspace — a dedicated private instance, priority models, and unlimited memory.'
                : 'Upgrade for a dedicated private instance, priority models, and unlimited memory.'}
            </p>
          ) : (
            <p className="mx-auto mt-4 max-w-[600px] text-base leading-relaxed text-gray-600">
              Xavior gives you the full workspace free for 14 days — modules, Google sync, and memory. Upgrade to keep it, get a private instance, or share a team space.
            </p>
          )}
        </div>
      )}

      {isAddWorkspace && (
        <div className="mb-4">
          <label htmlFor="pc-ws-name" className="mb-1 block text-xs font-medium text-gray-600">
            Workspace name <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            id="pc-ws-name"
            type="text"
            value={name}
            maxLength={50}
            onChange={(e) => {
              setName(e.target.value)
              onError(null)
            }}
            placeholder="e.g. Side Project, Acme LLP"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-sky-500"
          />
        </div>
      )}

      {!isAddWorkspace && <BillingToggle billing={billing} onChange={setBilling} />}

      <div className={`grid grid-cols-1 ${compact ? 'gap-3' : 'gap-4'} md:grid-cols-3`}>
        {isAddWorkspace ? (
          <PlanCard
            compact={compact}
            hideTerms
            orderClass="order-2 md:order-1"
            name="Free"
            badge="14-day trial"
            tagline="Your full workspace — free for 14 days."
            price="$0"
            period="/ 14 days"
            subPrice="No card required"
            dimmed={ownsFree}
            ctaText={
              submitting === 'free'
                ? 'Creating…'
                : ownsFree
                  ? 'Already have one'
                  : 'Create workspace'
            }
            ctaVariant="secondary"
            ctaDisabled={submitting !== null || ownsFree || containersLoading}
            note={
              ownsFree
                ? 'You already have a free workspace — upgrade or remove it to add another.'
                : undefined
            }
            onCta={() => createThenCheckout('free')}
            featuresHeading="What's included"
            features={[
              'Xavior AI Agent + all modules',
              'Google or Microsoft sync',
              'Persistent memory',
            ]}
          />
        ) : (
          <PlanCard
            compact={compact}
            orderClass="order-2 md:order-1"
            name="Trial"
            tagline={
              trialLapsed
                ? 'Your 14-day trial has ended. Upgrade to keep your workspace.'
                : 'The full product. Every module. Free for 14 days.'
            }
            price="$0"
            period="/ month"
            subPrice={trialLapsed ? 'Trial already used' : 'No credit card required'}
            dimmed={trialLapsed}
            ctaText={
              submitting === 'trial'
                ? 'Starting…'
                : trialLapsed
                  ? 'Trial ended'
                  : isUpgradeContext
                    ? 'Stay on Trial'
                    : 'Start Trial'
            }
            ctaVariant="secondary"
            ctaDisabled={submitting !== null || trialLapsed}
            onCta={startTrial}
            featuresHeading={isUpgradeContext ? 'Your current plan' : "What's included"}
            features={[
              'Xavior AI Agent',
              'All 10 modules',
              'Gmail, Calendar, Drive & Contacts sync',
              'Persistent memory across every conversation',
              'File and photo uploads',
            ]}
          />
        )}

        <PlanCard
          compact={compact}
          hideTerms={isAddWorkspace}
          orderClass="order-1 md:order-2"
          featured
          name="Pro"
          tagline="Your own private instance. Nothing shared with anyone."
          price={billing === 'yearly' ? '$25' : '$29'}
          period="/ month"
          subPrice={
            isAddWorkspace
              ? 'Billed monthly'
              : billing === 'yearly'
                ? 'Billed $300/year'
                : '$25/mo billed annually'
          }
          ctaText={submitting === 'pro' ? 'Starting…' : 'Start Pro'}
          ctaVariant="primary"
          ctaDisabled={submitting !== null}
          onCta={onProCta}
          featuresHeading="Everything in Free, plus"
          features={[
            ['Dedicated private instance', '— your data in its own container, never commingled'],
            'Priority access to the newest frontier models',
            'Unlimited memory and long-context sessions',
            'Priority response times',
            'Email & chat support',
          ]}
        />

        <PlanCard
          hidden={hideTeam}
          compact={compact}
          hideTerms={isAddWorkspace}
          orderClass="order-3 md:order-3"
          name="Team"
          tagline="Shared context for firms, offices, and partners."
          price={billing === 'yearly' ? '$39' : '$49'}
          period="/ seat / mo"
          subPrice={
            billing === 'yearly'
              ? `Billed annually · ${teamSeats} seats = $${teamSeats * 39 * 12}/yr`
              : `${teamSeats} seats = $${teamSeats * 49}/mo · min 2 seats`
          }
          ctaText={submitting === 'team' ? 'Starting…' : `Start Team — ${teamSeats} seats`}
          ctaVariant="secondary"
          ctaDisabled={submitting !== null}
          onCta={onTeamCta}
          featuresHeading="Everything in Pro, plus"
          features={[
            ['Shared contacts & CRM', 'across the team'],
            'Shared calendar intelligence & scheduling',
            'Shared Pages & collaborative memory',
            'Multi-seat workspace with invite + member management',
          ]}
          extraSlot={<SeatStepper seats={teamSeats} onChange={setTeamSeats} disabled={submitting !== null} />}
        />
      </div>
    </div>
  )
}
